import React from "react";
import {
    Box, Heading, Text, VStack, HStack, Badge, Icon, Flex, Button, Table,
    Thead, Tbody, Tr, Th, Td, IconButton
} from "@chakra-ui/react";
import { FiFileText, FiEye, FiDownload } from "react-icons/fi";
import { formatCurrency, formatInvoiceDate, getDueInBadge } from "../utils/formatHelpers";

const getInvoiceDownloadUrl = (pdfUrl) => {
    const match = pdfUrl?.match(/\/file\/d\/([^/]+)/);
    return match ? `https://drive.google.com/uc?export=download&id=${match[1]}` : pdfUrl;
};

export const InvoicesTab = ({ certifications, invoices, handleViewInvoice, isMobile }) => {
    const allInvoices = Object.values(invoices).flat();
    const totalUnpaid = allInvoices.filter(inv => inv.status !== 'paid' && inv.status !== 'Paid').length;

    return (
        <VStack spacing={6} align="stretch">
            {certifications.map((cert) => {
                const certInvoices = invoices[cert.id] || [];
                if (certInvoices.length === 0) return null;

                return (
                    <Box key={cert.id} p={{ base: 3, md: 5 }} bg="gray.50" borderRadius="lg" border="1px solid" borderColor="gray.200">
                        <HStack spacing={2} mb={{ base: 3, md: 4 }}>
                            <Icon as={FiFileText} color="brand.600" boxSize={{ base: 4, md: 5 }} />
                            <Heading size={{ base: "xs", md: "sm" }} color="brand.700">
                                {cert.full_certification?.name}
                            </Heading>
                        </HStack>

                        {/* Mobile Card View */}
                        <VStack spacing={2} align="stretch" display={{ base: "flex", md: "none" }}>
                            {certInvoices.map((invoice) => {
                                const isPaid = invoice.status === 'paid' || invoice.status === 'Paid';
                                const subtotalAmount = invoice.invoice_line_items?.length > 0
                                    ? invoice.invoice_line_items.reduce((sum, item) => sum + (item.unit_price * item.quantity), 0) : 0;
                                const gstAmount = invoice.gst_amount || 0;
                                const totalAmount = subtotalAmount + gstAmount;
                                const hasPdf = !!invoice.pdf_url;
                                const dueInBadge = getDueInBadge(invoice);
                                return (
                                    <Box
                                        key={invoice.id} bg="white" p={3} borderRadius="md"
                                        border="1px solid" borderColor={isPaid ? "green.200" : "gray.200"}
                                        borderLeft={isPaid ? "4px solid" : "1px solid"} borderLeftColor={isPaid ? "green.400" : "gray.200"}
                                        boxShadow="sm" cursor={hasPdf ? "pointer" : "default"}
                                        onClick={() => hasPdf && handleViewInvoice(invoice)}
                                        _hover={hasPdf ? { boxShadow: "md", transform: "translateY(-1px)", borderColor: isPaid ? "green.300" : "brand.400" } : {}}
                                        transition="all 0.2s"
                                    >
                                        <Flex justify="space-between" align="center" mb={2}>
                                            <HStack spacing={2} flex={1}>
                                                <Text fontWeight="bold" fontSize="sm" color="gray.800">{invoice.invoice_type}</Text>
                                                <Badge colorScheme={isPaid ? 'green' : 'orange'} fontSize="2xs" px={1.5} py={0.5}>{invoice.status}</Badge>
                                                {hasPdf && <Icon as={FiEye} boxSize="14px" color="brand.500" />}
                                            </HStack>
                                            <Text fontWeight="bold" fontSize="md" color={isPaid ? "green.600" : "gray.800"}>{formatCurrency(totalAmount)}</Text>
                                        </Flex>
                                        <VStack align="stretch" spacing={1.5}>
                                            <Flex justify="space-between"><Text fontSize="2xs" color="gray.500" fontWeight="medium">Invoice Date</Text><Text fontSize="xs" color="gray.700">{formatInvoiceDate(invoice.invoice_date)}</Text></Flex>
                                            <Flex justify="space-between" align="center"><Text fontSize="2xs" color="gray.500" fontWeight="medium">Due Date</Text><Text fontSize="xs" color="gray.700">{formatInvoiceDate(invoice.due_date)}</Text></Flex>
                                            <Flex justify="space-between" align="center"><Text fontSize="2xs" color="gray.500" fontWeight="medium">Due in</Text>{dueInBadge ? <Badge bg={dueInBadge.bg} color={dueInBadge.color} fontSize="2xs" px={2} py={0.5}>{dueInBadge.label}</Badge> : <Text fontSize="xs" color="gray.500">—</Text>}</Flex>
                                            {invoice.invoice_line_items?.length > 0 && (
                                                <Box><Text fontSize="2xs" color="gray.500" fontWeight="medium" mb={0.5}>Description</Text><Text fontSize="xs" color="gray.700" noOfLines={2}>{invoice.invoice_line_items.map(item => item.description).join(", ")}</Text></Box>
                                            )}
                                            {hasPdf && (
                                                <Flex justify="flex-end" pt={1} onClick={(e) => e.stopPropagation()}>
                                                    <HStack spacing={1}>
                                                        <IconButton
                                                            icon={<FiEye />}
                                                            size="xs"
                                                            variant="ghost"
                                                            colorScheme="brand"
                                                            aria-label="View PDF"
                                                            onClick={() => handleViewInvoice(invoice)}
                                                        />
                                                        <IconButton
                                                            as="a"
                                                            href={getInvoiceDownloadUrl(invoice.pdf_url)}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            icon={<FiDownload />}
                                                            size="xs"
                                                            variant="ghost"
                                                            colorScheme="teal"
                                                            aria-label="Download PDF"
                                                        />
                                                    </HStack>
                                                </Flex>
                                            )}
                                        </VStack>
                                    </Box>
                                );
                            })}
                        </VStack>

                        {/* Desktop Table View */}
                        <Box overflowX="auto" w="100%" display={{ base: "none", md: "block" }}>
                            <Table variant="simple" size="sm" bg="white">
                                <Thead bg="gray.100">
                                    <Tr>
                                        <Th>Invoice Date</Th><Th>Due Date</Th><Th>Due in</Th><Th>Description</Th>
                                        <Th isNumeric>Subtotal</Th><Th isNumeric>GST</Th><Th isNumeric>Total</Th><Th>Status</Th><Th>Actions</Th>
                                    </Tr>
                                </Thead>
                                <Tbody>
                                    {certInvoices.map((invoice) => {
                                        const isPaid = invoice.status === 'paid' || invoice.status === 'Paid';
                                        const subtotalAmount = invoice.invoice_line_items?.length > 0
                                            ? invoice.invoice_line_items.reduce((sum, item) => sum + (item.unit_price * item.quantity), 0) : 0;
                                        const gstAmount = invoice.gst_amount || 0;
                                        const totalAmount = subtotalAmount + gstAmount;
                                        const hasPdf = !!invoice.pdf_url;
                                        const dueInBadge = getDueInBadge(invoice);
                                        return (
                                            <Tr key={invoice.id}
                                                bg={isPaid ? "green.50" : "white"}
                                                _hover={{ bg: isPaid ? "green.100" : "gray.50", cursor: hasPdf ? "pointer" : "default" }}
                                                borderLeft={isPaid ? "4px solid" : "none"} borderColor={isPaid ? "green.400" : "transparent"}
                                                onClick={() => hasPdf && handleViewInvoice(invoice)} transition="all 0.2s"
                                            >
                                                <Td fontWeight={isPaid ? "semibold" : "normal"}>{formatInvoiceDate(invoice.invoice_date)}</Td>
                                                <Td fontWeight={isPaid ? "semibold" : "normal"}>{formatInvoiceDate(invoice.due_date)}</Td>
                                                <Td>{dueInBadge ? <Badge bg={dueInBadge.bg} color={dueInBadge.color} fontSize="xs" px={2} py={1}>{dueInBadge.label}</Badge> : <Text as="span" color="gray.400">—</Text>}</Td>
                                                <Td fontWeight={isPaid ? "semibold" : "normal"}>{invoice.invoice_line_items?.length > 0 ? invoice.invoice_line_items.map(item => item.description).join(", ") : "-"}</Td>
                                                <Td isNumeric fontWeight={isPaid ? "semibold" : "normal"}>{formatCurrency(subtotalAmount)}</Td>
                                                <Td isNumeric fontWeight={isPaid ? "semibold" : "normal"}>{formatCurrency(gstAmount)}</Td>
                                                <Td isNumeric fontWeight="bold" color={isPaid ? "green.600" : "gray.800"}>{formatCurrency(totalAmount)}</Td>
                                                <Td><Badge colorScheme={isPaid ? 'green' : 'orange'} fontSize="sm" px={2} py={1}>{invoice.status}</Badge></Td>
                                                <Td onClick={(e) => e.stopPropagation()}>
                                                    {hasPdf ? (
                                                        <HStack spacing={1}>
                                                            <IconButton
                                                                icon={<FiEye />}
                                                                size="sm"
                                                                variant="ghost"
                                                                colorScheme="brand"
                                                                aria-label="View invoice PDF"
                                                                title="View PDF"
                                                                onClick={() => handleViewInvoice(invoice)}
                                                            />
                                                            <IconButton
                                                                as="a"
                                                                href={getInvoiceDownloadUrl(invoice.pdf_url)}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                icon={<FiDownload />}
                                                                size="sm"
                                                                variant="ghost"
                                                                colorScheme="teal"
                                                                aria-label="Download invoice PDF"
                                                                title="Download PDF"
                                                            />
                                                        </HStack>
                                                    ) : (
                                                        <Text fontSize="xs" color="gray.400">Generating…</Text>
                                                    )}
                                                </Td>
                                            </Tr>
                                        );
                                    })}
                                </Tbody>
                            </Table>
                        </Box>
                    </Box>
                );
            })}

            {allInvoices.length === 0 && (
                <Box p={6} bg="white" borderRadius="md" textAlign="center">
                    <Text color="gray.500" fontSize="sm">No invoices available.</Text>
                </Box>
            )}
        </VStack>
    );
};
